// packages block
import { FC, createContext, useEffect, useState } from 'react'
import { Maybe, User, UserRole, useLoggedInUserLazyQuery } from '../../generated'
import { GRAPHQL_QUERY_POLICY, USER_EMAIL } from '../constants'
import { getToken } from '../utils/session'
// graphql, interfaces/types and constants block
import { CommonLoader } from '../components/common/CommonLoader'
import { AuthContextProps, ChildrenType } from '../interfaceTypes'

export const AuthContext = createContext<AuthContextProps>({
  currentUser: null,
  isLoggedIn: false,
  isAdmin: false,
  isSuperAdmin: false,
  isInvestigator: false,
  isAttorney: false,
  isLoading: false,
  isParalegal: false,
  isAccountant: false,
  isStaff: false,
  setIsLoggedIn: () => {
    /* 'setIsLoggedIn' is empty */
  },
  setIsLoading: () => {
    /* 'setIsLoading' is empty */
  },
  setIsAdmin: () => {
    /* 'setIsAdmin' is empty */
  },
  setIsSuperAdmin: () => {
    /* 'setIsSuperAdmin' is empty */
  },
  setIsInvestigator: () => {
    /* 'setIsInvestigator' is empty */
  },
  setIsAttorney: () => {
    /* 'setIsAttorney' is empty */
  },
  setIsParalegal: () => {
    /* 'setIsParalegal' is empty */
  },
  setIsAccountant: () => {
    /* 'setIsAccountant' is empty */
  },
  setUser: (currentUser: User | null) => {
    /* 'setUser' is empty */
  }
})

export const AuthContextProvider: FC<ChildrenType> = ({ children }) => {
  const [currentUser, setUser] = useState<Maybe<User>>(null)
  const [isLoading, _setIsLoading] = useState<boolean>(false)
  const [isLoggedIn, _setIsLoggedIn] = useState<boolean>(false)
  const [isAdmin, _setIsAdmin] = useState<boolean>(false)
  const [isSuperAdmin, _setIsSuperAdmin] = useState<boolean>(false)
  const [isInvestigator, _setIsInvestigator] = useState<boolean>(false)
  const [isAttorney, _setIsisAttorney] = useState<boolean>(false)
  const [isParalegal, _setIsParalegal] = useState<boolean>(false)
  const [isAccountant, _setIsAccountant] = useState<boolean>(false)
  const [isStaff, _setIsStaff] = useState<boolean>(false)
  const [isDcpp, _setIsDcpp] = useState<boolean>(false)

  const [fetchUser] = useLoggedInUserLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      setUser(null)
      _setIsLoading(false)
    },

    onCompleted(data) {
      if (data) {
        const {
          me: { user }
        } = data

        if (user) {
          setUser(user)
          const { email } = user
          localStorage.setItem(USER_EMAIL, email || '')
        }
      }
    }
  })

  const setIsAdmin = (isAdmin: boolean) => _setIsAdmin(isAdmin)
  const setIsSuperAdmin = (isSuperAdmin: boolean) => _setIsSuperAdmin(isSuperAdmin)
  const setIsInvestigator = (isInvestigator: boolean) => _setIsInvestigator(isInvestigator)
  const setIsAttorney = (isAttorney: boolean) => _setIsisAttorney(isAttorney)
  const setIsParalegal = (isParalegal: boolean) => _setIsParalegal(isParalegal)
  const setIsLoggedIn = (isLoggedIn: boolean) => _setIsLoggedIn(isLoggedIn)
  const setIsLoading = (isLoading: boolean) => _setIsLoading(isLoading)
  const setIsAccountant = (isAccountant: boolean) => _setIsAccountant(isAccountant)
  const setIsStaff = (isStaff: boolean) => _setIsStaff(isStaff)
  const setIsDcpp = (isDcpp: boolean) => _setIsDcpp(isDcpp)
  // An expired token counts as none, so a reload after expiry neither restores the session
  // nor asks the API for a user it will refuse.
  const hasToken = getToken()

  useEffect(() => {
    hasToken && _setIsLoggedIn(true)
    if (isLoggedIn && hasToken && !currentUser) {
      _setIsLoading(true)
      fetchUser()
    }

    if (currentUser) {
      const { roles } = currentUser || {}
      const admin = roles?.filter(item => item?.role === UserRole.Admin || item?.role === UserRole.SuperAdmin)
      const superAdmin = roles?.filter(item => item?.role === UserRole.SuperAdmin)
      const investigator = roles?.filter(item => item?.role === UserRole.Investigator)
      const attorney = roles?.filter(item => item?.role === UserRole.Attorney)
      const paralegal = roles?.filter(item => item?.role === UserRole.Paralegal)
      const accountant = roles?.filter(item => item?.role === UserRole.Accountant)
      const staff = roles?.filter(item => item?.role === UserRole.Staff)
      const dcpp = roles?.filter(item => item?.role === UserRole.Dcpp)

      setIsInvestigator(investigator?.length !== 0)
      setIsAttorney(attorney?.length !== 0)
      setIsAdmin(admin?.length !== 0)
      setIsSuperAdmin(superAdmin?.length !== 0)
      setIsParalegal(paralegal?.length !== 0)
      setIsAccountant(accountant?.length !== 0)
      setIsStaff(staff?.length !== 0)
      setIsDcpp(dcpp?.length !== 0)
      _setIsLoading(false)
    }
  }, [isLoggedIn, hasToken, currentUser, fetchUser])

  if (isLoading) return <CommonLoader />

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        setIsLoggedIn,
        setIsAdmin,
        isAdmin,
        currentUser,
        setUser,
        isInvestigator,
        setIsInvestigator,
        setIsAttorney,
        isAttorney,
        isLoading,
        isParalegal,
        setIsParalegal,
        setIsLoading,
        isSuperAdmin,
        setIsSuperAdmin,
        isAccountant,
        setIsAccountant,
        isStaff,
        setIsStaff,
        isDcpp,
        setIsDcpp
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
